import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from 'components/badge/Badge';
import { Button } from 'components/button/Button';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SearchInput } from 'components/search/SearchInput';
import { usePopup } from 'custom_hooks/usePopup';
import * as React from 'react';
import { isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import useResizeAware from 'react-resize-aware';
import { BadgeConfig } from 'shared/badge_icon/BadgeIcon';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const MultiSelectWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid ${tokens.border.default};
`;

const MultiSelectButton = styled(Button)`
    text-align: left;
    font-weight: 500;
    font-size: 1.4rem;
`;

const FilterMultiSelectButton = styled(MultiSelectButton)`
    padding: 1.5rem;
    display: flex;
    flex-grow: 1;
    flex-wrap: nowrap;
`;

const FilterLabel = styled.div`
    flex-grow: 1;
`;

export type MultiSelectPopupProps = {
    children?: React.ReactElement<{ filter: string; title?: string }>;
    style?: React.CSSProperties;
    className?: string;
    popupClassName?: string;
    /**
     * Class applied to the main content of the popup containing the multiselect.
     */
    contentClassName?: string;
    /**
     * The label text to display on the button that triggers the multiselect popup.
     */
    label: string;
    /**
     * Callback that will be called when the clear button to clear the currently
     * selected options is clicked. If not supplied, the clear button will not be shown.
     */
    onClear?: () => void;
    /**
     * When enabled, a search input to filter the options will be rendered in the header of the popup.
     */
    searchable?: boolean;
    /**
     * Placeholder for the search input element. Defaults to 'Search filters'.
     */
    searchPlaceholder?: string;
    onFilterChange?: (filter: string) => void;
    /**
     * If disabled, the popup cannot be triggered.
     */
    disabled?: boolean;
    filterCount?: number;
};

/**
 * Puts a MultiSelect (grouped or nongrouped) inside of a Popup and adds a filter
 * input to the multiselect. Trying to add another filter is undefined behavior at
 * the moment.
 */
export function MultiSelectPopup({
    children,
    style,
    label,
    onClear,
    onFilterChange,
    className,
    searchable,
    searchPlaceholder = 'Search filters',
    disabled,
    popupClassName,
    contentClassName,
    filterCount,
}: MultiSelectPopupProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup();
    const [filter, setFilter] = useState('');
    // Used to make the popup the same size as the trigger element
    const [resizeListener, sizes] = useResizeAware();

    const multiselect =
        React.isValidElement(children) &&
        React.cloneElement(children, {
            filter,
        });

    const noOverlay = true;

    useEffect(() => {
        if (isOpen) {
            window.setTimeout(() => inputRef.current?.focus());
        }
    }, [isOpen]);

    const badgeConfig = useMemo<BadgeConfig | undefined>(
        () =>
            filterCount && filterCount !== 0
                ? {
                      count: filterCount,
                      textColor: tokens.on.primary,
                      backgroundColor: tokens.text.link,
                  }
                : undefined,
        [filterCount],
    );

    return (
        <div className={className} style={{ position: 'relative' }}>
            {resizeListener}
            <MultiSelectWrapper>
                <FilterMultiSelectButton
                    type="button"
                    disabled={disabled}
                    onClick={toggleOpen}
                    ref={triggerRef}
                >
                    <FilterLabel>{label}</FilterLabel>
                    {(!isOpen || !onClear) &&
                        (badgeConfig ? (
                            <span
                                style={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                                <FontAwesomeIcon className="pl-15" icon={faFilter} />
                                <Badge
                                    className="ml-5"
                                    count={filterCount}
                                    backgroundColor={tokens.text.link}
                                    textColor={tokens.on.primary}
                                ></Badge>
                            </span>
                        ) : (
                            <FontAwesomeIcon className="pl-15" icon={faFilter} />
                        ))}
                    {isOpen && onClear && (
                        <MultiSelectButton type="button" onClick={onClear}>
                            Clear
                            <FontAwesomeIcon className="pl-10" icon={faTimes} />
                        </MultiSelectButton>
                    )}
                </FilterMultiSelectButton>
            </MultiSelectWrapper>
            {isOpen && (
                <StatelessPopup
                    onClose={close}
                    noPadding
                    popupClassName={popupClassName}
                    popupSettings={{
                        style: {
                            width: sizes.width ?? 0,
                            // Take into account border of trigger button border
                            // This is mostly to make the button borders look nice. If searchable, then there will be an extra
                            // border in the popup. Otherwise, there will not be a border, so we give some margin.
                            marginTop: searchable ? 0 : 2,
                            ...style,
                        },
                    }}
                    aria-label={isValidElement(multiselect) && multiselect.props.title}
                    ref={popupRef}
                    anchor={triggerRef.current}
                    noOverlay={noOverlay}
                >
                    {searchable && (
                        <SearchInput
                            ref={inputRef}
                            name="filter"
                            value={filter}
                            placeholder={searchPlaceholder}
                            onChange={(event) => {
                                setFilter(event.target.value);
                                if (onFilterChange) {
                                    onFilterChange(event.target.value);
                                }
                            }}
                            inputSize="large"
                            autofocus
                        />
                    )}
                    <div className={contentClassName}>{multiselect}</div>
                </StatelessPopup>
            )}
        </div>
    );
}
